import { StatusChip } from "../ui/status-chip.js";

/**
 * The cell vocabulary of the attendance matrix, shared by the two views that draw
 * one: All Team's employee x day grid (MonthlyAttendanceGrid) and My History's
 * month x day-of-month register (AttendanceRegister). Both read the same
 * /attendance/monthly-grid payload, so the statuses, tones, hover text and legend
 * live here once rather than being restated per view.
 */

export type CellStatus =
  | "SUMMARY" | "NOT_EMPLOYED" | "FUTURE" | "PRESENT" | "PRESENT_INCOMPLETE" | "PENDING_APPROVAL" | "HALF_DAY"
  | "HOLIDAY" | "LEAVE" | "OFF_DAY" | "ABSENT";

export interface GridCell {
  status: CellStatus;
  label: string;
  rejected?: boolean;
  checkIn?: string;
  checkOut?: string;
  note?: string;
}

export interface GridRow {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  departmentName: string | null;
  officeName: string | null;
  cells: GridCell[];
  totals: { present: number; absent: number; leave: number; holiday: number; off: number };
}

export interface MonthlyGrid {
  from: string;
  to: string;
  granularity: "day" | "month";
  days: string[];
  rows: GridRow[];
  total: number;
  page: number;
  limit: number;
}

export type ChipTone = "present" | "absent" | "leave" | "halfDay" | "holiday" | "off" | "none";

export const TONE_BY_STATUS: Record<CellStatus, ChipTone> = {
  // A rolled-up month cell carries a count, not a status, so it stays neutral.
  SUMMARY: "off",
  PRESENT: "present",
  PRESENT_INCOMPLETE: "present",
  // Worked, but not counted until approved: the amber half-day tone, not the present green.
  PENDING_APPROVAL: "halfDay",
  ABSENT: "absent",
  LEAVE: "leave",
  HALF_DAY: "halfDay",
  HOLIDAY: "holiday",
  OFF_DAY: "off",
  NOT_EMPLOYED: "none",
  FUTURE: "none",
};

/** The statuses that count as the employee having worked that day. */
export const PRESENT_STATUSES: ReadonlySet<CellStatus> = new Set<CellStatus>([
  "PRESENT", "PRESENT_INCOMPLETE", "HALF_DAY",
]);

export const LEGEND: Array<{ tone: ChipTone; label: string; text: string }> = [
  { tone: "present", label: "P", text: "Present" },
  { tone: "absent", label: "A", text: "Absent" },
  { tone: "leave", label: "L", text: "Leave" },
  { tone: "halfDay", label: "½", text: "Half day" },
  { tone: "halfDay", label: "?", text: "Awaiting approval" },
  { tone: "holiday", label: "H", text: "Holiday" },
  { tone: "off", label: "", text: "Off day" },
];

export function timeLabel(iso: string | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** The cell's hover text. Composed here rather than server-side so times render in
 *  the viewer's locale; the API sends the raw instants and a note. */
export function cellTitle(cell: GridCell, day: string): string {
  const parts = [day];
  const inAt = timeLabel(cell.checkIn);
  const outAt = timeLabel(cell.checkOut);
  if (inAt) parts.push(outAt ? `${inAt} - ${outAt}` : `In ${inAt}`);
  if (cell.note) parts.push(cell.note);
  if (cell.status === "ABSENT") parts.push("Absent");
  if (cell.status === "OFF_DAY") parts.push("Off day");
  if (cell.status === "NOT_EMPLOYED") parts.push("Not employed");
  if (cell.rejected) parts.push("Correction request rejected");
  return parts.join(" · ");
}

/** The swatch row under (or over) a matrix. `children` adds view-specific controls
 *  on the same line, e.g. All Team's Export CSV button. */
export function AttendanceLegend({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-2.5">
      {LEGEND.map((l) => (
        <span key={l.text} className="flex items-center gap-1.5 text-xs text-surface-600">
          <StatusChip tone={l.tone} size="legend">{l.label}</StatusChip>
          {l.text}
        </span>
      ))}
      <span className="flex items-center gap-1.5 text-xs text-surface-600">
        <StatusChip tone="absent" size="legend" marker />
        Correction rejected
      </span>
      {children}
    </div>
  );
}


